import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Slider,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import {
  useTenantLogo,
  useUploadTenantLogo,
  useUpdateTenantBranding,
  useRemoveTenantLogo,
  LogoBackdrop,
} from '../../api/admin';
import { useTenantProfile, useUpdateTenantProfile } from '../../api/tenantProfile';
import { asRequestError, requestFieldErrors, requestMessage } from '../../utils/requestError';
import { useAuth } from '../../contexts/AuthContext';
import TenantLogoBadge from '../TenantLogoBadge';
import AdminPageHeader from './AdminPageHeader';

const ACCEPT = 'image/png,image/jpeg,image/svg+xml,image/webp';
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

// A preview of how the logo sits in the (dark) top bar, beside the FiSci logo.
function TopBarPreview({
  logoSrc,
  backdrop,
  scale,
}: {
  logoSrc: string | null;
  backdrop: LogoBackdrop;
  scale: number;
}) {
  return (
    <Box
      sx={{
        height: 50,
        bgcolor: '#212b37',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 2,
        border: '1px solid rgba(0,0,0,0.2)',
      }}
    >
      <Box component="img" src="/logo.png" alt="FiSci" sx={{ height: 30, width: 'auto' }} />
      {logoSrc && (
        <>
          <Box sx={{ width: '1px', height: 26, bgcolor: 'rgba(255,255,255,0.22)' }} />
          <TenantLogoBadge src={logoSrc} backdrop={backdrop} scale={scale} />
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
        top bar preview
      </Typography>
    </Box>
  );
}

export default function BrandingManagement() {
  const { data: branding, isLoading } = useTenantLogo();
  const uploadLogo = useUploadTenantLogo();
  const updateBranding = useUpdateTenantBranding();
  const removeLogo = useRemoveTenantLogo();
  const { updateUser } = useAuth();
  const { data: profile, isLoading: profileLoading } = useTenantProfile();
  const updateProfile = useUpdateTenantProfile();

  // Organisation name + short name, edited together and saved with their own button.
  const [nameDraft, setNameDraft] = useState('');
  const [slugDraft, setSlugDraft] = useState('');
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [profileError, setProfileError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setNameDraft(profile.name);
      setSlugDraft(profile.slug);
      setProfileErrors({});
    }
  }, [profile]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [backdrop, setBackdrop] = useState<LogoBackdrop>('none');
  const [scale, setScale] = useState<number>(100);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const currentLogo = branding?.logo_url ?? null;
  const savedBackdrop = branding?.backdrop ?? 'none';
  const savedScale = branding?.scale ?? 100;
  const previewSrc = pendingPreview ?? currentLogo;

  // Sync the local selections with the server values when they (re)load.
  useEffect(() => {
    if (branding) {
      setBackdrop(branding.backdrop);
      setScale(branding.scale);
    }
  }, [branding]);

  const dirty = !!pendingFile || backdrop !== savedBackdrop || scale !== savedScale;

  const handleFile = (file: File | undefined | null) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please choose a PNG, JPG, SVG or WEBP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Logo must be 2 MB or smaller.');
      return;
    }
    setError(null);
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPendingPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      const result = pendingFile
        ? await uploadLogo.mutateAsync({ file: pendingFile, backdrop, scale })
        : await updateBranding.mutateAsync({ backdrop, scale });
      updateUser({
        tenant_logo_url: result.logo_url,
        tenant_logo_backdrop: result.backdrop,
        tenant_logo_scale: result.scale,
      }); // refresh the live top bar
      setPendingFile(null);
      setPendingPreview(null);
      setToast(
        pendingFile
          ? 'Logo saved. It now appears in the top bar for everyone in your organisation.'
          : 'Logo appearance updated.'
      );
    } catch (e: unknown) {
      setError(asRequestError(e).data?.message || requestMessage(e, 'Failed to save branding.'));
    }
  };

  const handleRemove = async () => {
    try {
      const result = await removeLogo.mutateAsync();
      updateUser({
        tenant_logo_url: result.logo_url,
        tenant_logo_backdrop: result.backdrop,
        tenant_logo_scale: result.scale,
      });
      setPendingFile(null);
      setPendingPreview(null);
      setBackdrop('none');
      setScale(100);
      setToast('Logo removed.');
    } catch (e: unknown) {
      setError(asRequestError(e).data?.message || requestMessage(e, 'Failed to remove logo.'));
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const profileDirty =
    !!profile && (nameDraft.trim() !== profile.name || slugDraft.trim() !== profile.slug);
  const canEditProfile = !!profile?.can_edit;

  const handleSaveProfile = () => {
    if (!profile) return;
    setProfileErrors({});
    setProfileError(null);
    const body: { name?: string; slug?: string } = {};
    if (nameDraft.trim() !== profile.name) body.name = nameDraft.trim();
    if (slugDraft.trim() !== profile.slug) body.slug = slugDraft.trim();
    updateProfile.mutate(body, {
      onSuccess: (result) => {
        // The top bar and account menu show the organisation's name.
        updateUser({ tenant_name: result.name, tenant_slug: result.slug });
        setToast(
          body.slug !== undefined
            ? 'Organisation saved. Share the new sign-in link with your people.'
            : 'Organisation saved.'
        );
      },
      onError: (err: unknown) => {
        const fieldErrors = requestFieldErrors(err);
        setProfileErrors(fieldErrors);
        if (!Object.keys(fieldErrors).length) {
          setProfileError(requestMessage(err, 'Could not save the organisation details.'));
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const saving = uploadLogo.isPending || updateBranding.isPending;
  const removing = removeLogo.isPending;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <AdminPageHeader
        title="Profile & branding"
        description="Your organisation's name and the logo shown beside Mitigate's in the top bar."
        audience="admins"
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
      {/* Left column: the organisation's details and the logo settings. */}
      <Box sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organisation
          </Typography>
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError(null)}>
              {profileError}
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Organisation name"
            value={nameDraft}
            onChange={(e) => {
              setNameDraft(e.target.value);
              setProfileErrors((prev) => ({ ...prev, name: '' }));
            }}
            disabled={profileLoading || !canEditProfile || updateProfile.isPending}
            error={!!profileErrors.name}
            helperText={profileErrors.name || 'Shown in the top bar and account menu for everyone in your organisation.'}
          />
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Short name (sign-in link)"
            value={slugDraft}
            onChange={(e) => {
              setSlugDraft(e.target.value);
              setProfileErrors((prev) => ({ ...prev, slug: '' }));
            }}
            disabled={profileLoading || !canEditProfile || updateProfile.isPending}
            error={!!profileErrors.slug}
            helperText={
              profileErrors.slug ||
              `Your sign-in link is ${window.location.origin}/login/${slugDraft.trim() || '…'}. Also shown under Sign-in.`
            }
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Box sx={{ flexGrow: 1 }} />
            {profileDirty && (
              <Button
                variant="text"
                onClick={() => {
                  if (!profile) return;
                  setNameDraft(profile.name);
                  setSlugDraft(profile.slug);
                  setProfileErrors({});
                  setProfileError(null);
                }}
                disabled={updateProfile.isPending}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              sx={{ color: 'white' }}
              onClick={handleSaveProfile}
              disabled={!profileDirty || !canEditProfile || updateProfile.isPending}
            >
              {updateProfile.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Paper>

        <Typography variant="h6" gutterBottom>
          Logo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload your organisation's logo. It appears in the top bar next to the Mitigate logo for
          everyone in your organisation. Use a PNG or SVG with a transparent background. If your logo
          is dark, add a white background below so it stays visible on the dark bar.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          {/* backdrop selector */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
              LOGO BACKGROUND
            </Typography>
            <ToggleButtonGroup
              value={backdrop}
              exclusive
              color="secondary"
              size="small"
              onChange={(_e, v: LogoBackdrop | null) => { if (v) setBackdrop(v); }}
            >
              <ToggleButton value="none">None</ToggleButton>
              <ToggleButton value="square">White square</ToggleButton>
              <ToggleButton value="circle">White circle</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Adds a white backing behind a dark logo so it reads on the dark top bar.
            </Typography>
          </Box>

          {/* scale slider */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
              LOGO SIZE — {scale}%
            </Typography>
            <Box sx={{ px: 1, maxWidth: 360 }}>
              <Slider
                value={scale}
                onChange={(_e, v) => setScale(v as number)}
                min={50}
                max={150}
                step={5}
                marks={[
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' },
                  { value: 150, label: '150%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                color="secondary"
                disabled={!previewSrc}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {backdrop === 'none'
                ? 'Adjust how large your logo appears in the top bar.'
                : 'Zooms the logo inside the fixed shape and crops the overflow.'}
            </Typography>
          </Box>

          {/* dropzone */}
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onDrop={onDrop}
            sx={{
              mt: 3,
              border: `2px dashed ${dragActive ? '#00d4ad' : '#e0e0e0'}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: dragActive ? '#f0fdf8' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: '#00d4ad', backgroundColor: '#f0fdf8' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: '#00d4ad' }} />
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              Drag &amp; drop your logo here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              or
            </Typography>
            <Button variant="contained" color="secondary" sx={{ color: 'white' }}>
              Choose file
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              PNG, JPG, SVG or WEBP · up to 2 MB
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemove}
              disabled={removing || (!currentLogo && !pendingFile)}
            >
              {removing ? 'Removing…' : 'Remove logo'}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {dirty && (
              <Button
                variant="text"
                onClick={() => {
                  setPendingFile(null);
                  setPendingPreview(null);
                  setBackdrop(savedBackdrop);
                  setScale(savedScale);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              sx={{ color: 'white' }}
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Right column: how the logo looks in the top bar, kept in view while editing. */}
      <Box sx={{ position: { lg: 'sticky' }, top: 88, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
            PREVIEW
          </Typography>
          <TopBarPreview logoSrc={previewSrc} backdrop={backdrop} scale={scale} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {previewSrc
              ? 'How the top bar looks for everyone in your organisation once saved.'
              : 'No logo uploaded yet — only the Mitigate logo is shown.'}
          </Typography>
        </Paper>
      </Box>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
